import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { PatientService } from '../../services';
import { ConfirmationService, ModalService } from '../../services';
import { CONTACT_ADDRESS_MAX_LENGTH } from '../../validators';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-right-emergency-panel',
  templateUrl: './right-emergency-panel.component.html',
  styleUrls: ['./right-emergency-panel.component.scss']
})
export class RightEmergencyPanelComponent implements OnInit, OnChanges {

  @Input() contact: any;
  @Input() imei: any;
  @Output() closeEmergencyPanel: EventEmitter<void> = new EventEmitter();
  @Output() contactSaved: EventEmitter<any> = new EventEmitter();
  @Output() contactDeleted: EventEmitter<any> = new EventEmitter();

  contactForm: FormGroup;
  isPanelVisible = false;
  isSaving = false;
  isDeleting = false;

  constructor(
    private fb: FormBuilder,
    private patientService: PatientService,
    private modalService: ModalService,
    private confirmation: ConfirmationService,
    private translate: TranslateService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.updateFormValues();
    setTimeout(() => {
      this.isPanelVisible = true;
    }, 50);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contact'] && !changes['contact'].firstChange) {
      this.updateFormValues();
    }
  }

  private initForm(): void {
    this.contactForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(32)]],
      family_kind: ['', [Validators.required, Validators.maxLength(32)]],
      phone_num: ['', [
        Validators.required,
        Validators.minLength(4),
        Validators.maxLength(15),
        Validators.pattern('([1-9\\+][0-9]*)$')
      ]],
      // address nie ma pola w szablonie panelu — required zablokowałby zapis
      address: ['', [Validators.maxLength(CONTACT_ADDRESS_MAX_LENGTH)]],
      others: [false]
    });
  }

  private updateFormValues(): void {
    if (this.contact && this.contactForm) {
      this.contactForm.patchValue({
        name: this.contact.name || '',
        family_kind: this.contact.family_kind || '',
        phone_num: this.contact.phone_num || '',
        address: this.contact.address || '',
        others: this.contact.others === '1' || this.contact.others === 1 || this.contact.others === true
      });
    }
  }

  closePanel(): void {
    this.isPanelVisible = false;
    setTimeout(() => {
      this.closeEmergencyPanel.emit();
    }, 100);
  }

  saveContact(): void {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }
    if (!this.isSaving) {
      this.isSaving = true;

      const formValue = this.contactForm.value;

      const payload = {
        contactID: this.contact?.id || null,
        contactName: formValue.name,
        contactAddress: formValue.address || '',
        contactPhone: formValue.phone_num,
        contactAffinity: formValue.family_kind,
        contactOthers: formValue.others ? '1' : '0'
      };

      this.patientService.editContactsHealth(this.imei, payload).subscribe({
        next: (response) => {
          this.isSaving = false;
          this.confirmation.show('COMMON.CONTACT_SAVED', undefined, 'EVENTS.SHARED.COMPONENTS.RIGHT-EMERGENCY-PANEL.POPUP.COMMON-CONTACT_SAVED');
          this.contactSaved.emit(payload);
          this.closePanel();
        },
        error: (err) => {
          this.isSaving = false;

          if (err.text === 'OK' || err.status === 200) {
            this.confirmation.show('COMMON.CONTACT_SAVED', undefined, 'EVENTS.SHARED.COMPONENTS.RIGHT-EMERGENCY-PANEL.POPUP.COMMON-CONTACT_SAVED');
            this.contactSaved.emit(payload);
            this.closePanel();
          } else {
            this.modalService.open(this.translate.instant('BACKEND.SOMETHING-WRONG'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.RIGHT-EMERGENCY-PANEL.POPUP.BACKEND-SOMETHING-WRONG.SAVE-CONTACT');
            console.error('Błąd zapisu kontaktu:', err);
          }
        }
      });
    }
  }

  deleteContact(): void {
    if (!this.contact?.id || this.isDeleting) {
      return;
    }

    this.modalService.openAccept(
      this.translate.instant('COMMON.CONFIRM_DELETE_CONTACT'),
      'EVENTS.SHARED.COMPONENTS.RIGHT-EMERGENCY-PANEL.POPUP.COMMON-CONFIRM_DELETE_CONTACT'
    ).subscribe(data => {
      if (data.ok) {
        this.isDeleting = true;

        this.patientService.deleteContact(this.contact.id, this.imei).subscribe({
          next: () => {
            this.isDeleting = false;
            this.confirmation.show('COMMON.CONTACT_DELETED', undefined, 'EVENTS.SHARED.COMPONENTS.RIGHT-EMERGENCY-PANEL.POPUP.COMMON-CONTACT_DELETED');
            this.contactDeleted.emit(this.contact);
            this.closePanel();
          },
          error: (err) => {
            this.isDeleting = false;

            if (err.text === 'OK' || err.status === 200) {
              this.confirmation.show('COMMON.CONTACT_DELETED', undefined, 'EVENTS.SHARED.COMPONENTS.RIGHT-EMERGENCY-PANEL.POPUP.COMMON-CONTACT_DELETED');
              this.contactDeleted.emit(this.contact);
              this.closePanel();
            } else {
              this.modalService.open(this.translate.instant('BACKEND.SOMETHING-WRONG'), undefined, undefined, 'EVENTS.SHARED.COMPONENTS.RIGHT-EMERGENCY-PANEL.POPUP.BACKEND-SOMETHING-WRONG.DELETE-CONTACT');
              console.error('Błąd usuwania kontaktu:', err);
            }
          }
        });
      }
    });
  }
}
